/* =============================================================================
   The localisation map - the lookup layer over the locale catalogs that sit
   beside it. It turns a string KEY into the text the user sees, so a caller
   asks for `t('messager.ok')` rather than carrying a literal, and a wording
   change is a catalog edit in one place.

   RESOLUTION ORDER, always three steps:
     the ACTIVE locale's catalog -> the en base string -> the key itself.
   So a missing translation degrades to English and a missing key degrades to
   the key. Neither ever degrades to a blank, because a blank caption is
   indistinguishable from a broken screen.

   TWO KINDS OF ENTRY LIVE HERE (ruling D3, 2026-09-04)
     a STRING  'messager.ok': 'Ok'
               a component caption. What this file has always held.
     an OBJECT { text, tokens?, requiredTokens?, severity, appearance, title? }
               a PRODUCT MESSAGE - the text plus how it is shown. The 92 of them
               are GENERATED into ../messages.en.js from messages.master.json and
               registered by the messager bundle (see the realm note below).
   `t(key)` answers a string for BOTH - an object entry yields its `.text`, so
   every existing caller is unaffected. `entry(key)` is the new accessor that
   returns the whole object, and it is what IcsMsg() dispatches on.

   SLOT SUBSTITUTION, identical to the C# half (ESimSol.HMI Msg.Render) so the
   two runtimes cannot disagree: the caller's value, then the entry's declared
   default, then the slot STAYS LITERAL. That last branch is deliberate - a
   required slot nobody supplied must be visible as {slot}, not swallowed into a
   sentence that reads fine and means nothing.

   ⚠⚠ THE CATALOG STORE IS REALM-SHARED, on globalThis[Symbol.for('ics.i18n')].
   esbuild compiles this module separately into EVERY bundle that imports it
   (today: dialogs, jobstatus, and anything pulling services/validation.js), so a
   plain module-level Map would give each bundle its own catalogs - and the 92
   product messages, registered by ONE bundle, would be invisible to the others
   with no throw and no console line. That is precisely what the window stack
   paid for (frontend-kb 9.12). Registering them once, in the messager bundle,
   also keeps the generated payload out of every other bundle's bytes.

   LOADING: the en catalog is bundled, every other catalog lazy-imports when
   its locale is loaded, so an install never parses catalogs it does not use.
   A locale with no catalog registered runs entirely on the en fallback - that
   is a supported state, not an error.

   API: load(id) (async) · extend(id, entries) · t(key[, tokens]) · entry(key) ·
   render(entry[, tokens]), plus the bound `t` convenience export callers import
   directly.
   ============================================================================= */
import { LocaleService } from '../../services/locale.js';
import en from '../en.js';

/* one store per PAGE, not per bundle - see the realm note above */
const REALM = Symbol.for('ics.i18n');
function store() {
  let s = globalThis[REALM];
  if (!s) {
    s = globalThis[REALM] = { catalogs: new Map() };
    s.catalogs.set('en', Object.assign({}, en));      // a COPY: extend() must never mutate the module
  } else if (!s.catalogs.has('en')) {
    s.catalogs.set('en', Object.assign({}, en));
  } else {
    /* another bundle seeded the store first; make sure this bundle's own en
       strings are present too, without clobbering anything already registered */
    const base = s.catalogs.get('en');
    for (const k of Object.keys(en)) if (!(k in base)) base[k] = en[k];
  }
  return s;
}

const _loaders = {
  bn: () => import('../bn.js'),
  /* further loaders are registered here as their catalogs are authored */
};

/** the raw catalog value for a key: active locale, then the en base, else undefined */
function lookup(key) {
  const c = store().catalogs;
  const active = c.get(LocaleService.active);
  if (active && key in active) return active[key];
  const base = c.get('en');
  if (base && key in base) return base[key];
  return undefined;
}

export const MessageCatalogService = {
  /** ensure the catalog for a locale is loaded (lazy for every non-base locale);
      a locale with no registered loader resolves silently and uses the fallback */
  async load(id) {
    const c = store().catalogs;
    if (c.has(id) || !_loaders[id]) return;
    const mod = await _loaders[id]();
    c.set(id, Object.assign({}, mod.default || {}));
  },

  /** register or extend a catalog at runtime (a component adding its own strings,
      or the messager bundle registering the generated product dictionary) */
  extend(id, entries) {
    const c = store().catalogs;
    c.set(id, Object.assign(c.get(id) || {}, entries));
  },

  /** Register entries WITHOUT overwriting anything the catalog already holds -
      how the GENERATED product dictionary is registered. On a key held by both,
      the hand-authored catalog wins, because that one is a component's contract
      with the screens. The generator refuses to emit a colliding key at all, so
      this is a guard rather than a routine path; it returns what it refused and
      says so loudly, since a silently dropped message is the failure to avoid. */
  extendIfAbsent(id, entries) {
    const c = store().catalogs;
    const cat = c.get(id) || {};
    const refused = [];
    for (const k of Object.keys(entries)) {
      if (k in cat) { refused.push(k); continue; }
      cat[k] = entries[k];
    }
    c.set(id, cat);
    if (refused.length) console.error('[ics] i18n: generated keys NOT registered (the catalog already holds them): ' + refused.join(', '));
    return refused;
  },

  /**
   * The full entry for a PRODUCT MESSAGE key, or null.
   * A string entry (a component caption) is deliberately NOT wrapped into one:
   * a caption is not a message and must not be dispatched as a toast. IcsMsg
   * handles that case itself, loudly.
   */
  entry(key) {
    const v = lookup(key);
    if (!v || typeof v !== 'object') return null;
    return {
      key,
      text: v.text === undefined ? key : String(v.text),
      tokens: v.tokens || null,
      requiredTokens: v.requiredTokens || null,
      severity: v.severity || 'info',
      appearance: v.appearance || 'toast',
      title: v.title || null,
    };
  },

  /** fill the {slots} of an entry (or a bare string) - the shared substitution rule */
  render(entryOrText, tokens) {
    const isObj = entryOrText && typeof entryOrText === 'object';
    const text = isObj ? String(entryOrText.text === undefined ? '' : entryOrText.text) : String(entryOrText);
    if (text.indexOf('{') < 0) return text;
    const defaults = isObj ? entryOrText.tokens : null;
    return text.replace(/\{(\w+)\}/g, (whole, slot) => {
      if (tokens && slot in tokens && tokens[slot] !== undefined && tokens[slot] !== null) return String(tokens[slot]);
      if (defaults && slot in defaults) return String(defaults[slot]);
      return whole;                                   // required and unsupplied: stays visible
    });
  },

  /**
   * Resolve a key to TEXT against the ACTIVE locale, falling back to en, then to
   * the key. Optional {tokens} interpolation: t('x.y', {name:'A'}) replaces every
   * {name}. An object entry yields its `.text` (with its own defaults applied),
   * so a caller written against the string catalog keeps working unchanged.
   */
  t(key, tokens) {
    const v = lookup(key);
    if (v === undefined) return key;
    return MessageCatalogService.render(v, tokens);
  },
};

/** convenience named export used across components */
export const t = MessageCatalogService.t.bind(MessageCatalogService);
